// StudyAdvicePanel.js — Personalized AI study advice panel
// Fetches struggle log + Q&A records, processes mastery history, calls gpt-4o-mini

import React, { useState } from 'react';
import MessageContent from './MessageContent';
import { apiFetch, API_BASE_URL } from '../config';

const StudyAdvicePanel = ({ masteryHistory, onClose, logAction }) => {
  const [advice, setAdvice] = useState(null); // { content, loading }

  const generate = async () => {
    setAdvice({ content: null, loading: true });
    const username = localStorage.getItem('username');
    logAction('Study Advice Opened', '', '');

    try {
      // Fetch struggle log + Q&A records in parallel
      const [struggleRes, qaRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/api/logStudentStruggle?username=${encodeURIComponent(username)}&limit=100`),
        apiFetch(`${API_BASE_URL}/api/studentQuestionAnsweredAll?username=${encodeURIComponent(username)}&limit=50`)
      ]);
      const struggleData = struggleRes.ok ? await struggleRes.json() : {};
      const struggleLogs = struggleData.struggle_log || [];
      const qaData = qaRes.ok ? await qaRes.json() : {};
      const qaRecords = (qaData.historyquestionanswered || qaData.questions || qaData.records || qaData.history || [])
        .slice(0, 50); // API returns created_at DESC — first 50 = latest 50

      // --- Quiz/Q&A summary from masteryHistory (last 50, sorted oldest→newest) ---
      const recentHistory = [...(masteryHistory || [])]
        .sort((a, b) => new Date(a.recorded_at || a.timestamp || a.date || a.last_updated || 0) - new Date(b.recorded_at || b.timestamp || b.date || b.last_updated || 0))
        .slice(-50);

      const quizStats = {};
      recentHistory.forEach(r => {
        if (!r.subject) return;
        if (!quizStats[r.subject]) quizStats[r.subject] = { correct: 0, wrong: 0, recent: [] };
        quizStats[r.subject].correct += r.correct || 0;
        quizStats[r.subject].wrong += r.wrong || 0;
        quizStats[r.subject].recent.push({ correct: r.correct || 0, wrong: r.wrong || 0 });
      });

      // --- Struggle log — split newest 50 vs older 50 for trend ---
      const recentLogs = struggleLogs.slice(0, Math.ceil(struggleLogs.length / 2));
      const olderLogs  = struggleLogs.slice(Math.ceil(struggleLogs.length / 2));

      const codeStats = {};
      const buildCodeStats = (logs, bucket) => {
        logs.forEach(log => {
          const t = log.topic || 'other';
          if (!codeStats[t]) codeStats[t] = {
            recent: { success: 0, errors: 0 },
            older:  { success: 0, errors: 0 },
            syntax: 0, logic: 0, runtime: 0
          };
          const type = (log.struggle_type || '').toUpperCase();
          if (type === 'SUCCESS') codeStats[t][bucket].success++;
          else {
            codeStats[t][bucket].errors++;
            if (type === 'SYNTAX_ERROR') codeStats[t].syntax++;
            else if (type === 'LOGIC_ERROR') codeStats[t].logic++;
            else if (type === 'RUNTIME_ERROR') codeStats[t].runtime++;
          }
        });
      };
      buildCodeStats(recentLogs, 'recent');
      buildCodeStats(olderLogs,  'older');

      // --- Recent 15 wrong answers from quiz history ---
      const recentWrong = recentHistory
        .filter(r => (r.wrong || 0) > 0)
        .slice(-15)
        .map(r => r.subject);

      // --- Build summaries ---
      const quizSummary = Object.entries(quizStats)
        .filter(([, s]) => s.correct + s.wrong >= 2)
        .sort(([, a], [, b]) => (b.wrong / (b.correct + b.wrong)) - (a.wrong / (a.correct + a.wrong)))
        .map(([subject, s]) => {
          const total = s.correct + s.wrong;
          const pct = Math.round((s.correct / total) * 100);
          const half = Math.floor(s.recent.length / 2);
          const earlyTotal = half > 0 ? s.recent.slice(0, half).reduce((a, r) => a + r.correct + r.wrong, 0) : 0;
          const lateTotal  = s.recent.slice(half).reduce((a, r) => a + r.correct + r.wrong, 0);
          const earlyPct = earlyTotal > 0 ? Math.round((s.recent.slice(0, half).reduce((a, r) => a + r.correct, 0) / earlyTotal) * 100) : pct;
          const latePct  = lateTotal  > 0 ? Math.round((s.recent.slice(half).reduce((a, r) => a + r.correct, 0)  / lateTotal)  * 100) : pct;
          const trend = latePct > earlyPct + 10 ? '📈 improving' : latePct < earlyPct - 10 ? '📉 declining' : '➡️ stable';
          return `- ${subject}: ${pct}% accuracy (${total} questions) ${trend}`;
        }).join('\n') || 'No quiz data yet.';

      const codeSummary = Object.entries(codeStats)
        .filter(([, s]) => s.recent.success + s.recent.errors + s.older.success + s.older.errors >= 2)
        .map(([topic, s]) => {
          const totalErrors = s.syntax + s.logic + s.runtime;
          const dominant = totalErrors === 0 ? null
            : s.syntax >= s.logic && s.syntax >= s.runtime ? 'syntax errors'
            : s.logic >= s.runtime ? 'logic errors' : 'runtime errors';
          const dominantStr = dominant ? ` (mostly ${dominant})` : '';
          const recentTotal = s.recent.success + s.recent.errors;
          const olderTotal  = s.older.success  + s.older.errors;
          const recentErrPct = recentTotal > 0 ? s.recent.errors / recentTotal : 0;
          const olderErrPct  = olderTotal  > 0 ? s.older.errors  / olderTotal  : 0;
          const codeTrend = recentErrPct < olderErrPct - 0.15 ? '📈 improving'
            : recentErrPct > olderErrPct + 0.15 ? '📉 getting worse' : '➡️ stable';
          return `- ${topic}: ${s.recent.success + s.older.success} success, ${totalErrors} errors${dominantStr} ${codeTrend}`;
        }).join('\n') || 'No code execution data yet.';

      const wrongSummary = recentWrong.length > 0
        ? recentWrong.map(s => `- ${s}`).join('\n')
        : 'None recorded.';

      const qaWrong = qaRecords.filter(r => r.correct === 'wrong');
      const qaSummary = qaRecords.length > 0
        ? qaWrong.length > 0
          ? qaWrong.slice(0, 20).map(r => `- [${r.subject || 'unknown'}]`).join('\n')
          : 'No wrong Q&A answers in last 50 interactions.'
        : 'No Q&A data yet.';

      // --- No data at all — skip LLM ---
      if (quizSummary === 'No quiz data yet.' && codeSummary === 'No code execution data yet.') {
        setAdvice({
          content: `## 🎯 No Data Yet\n\nIt looks like you haven't completed any quizzes, Q&A sessions, or code exercises yet.\n\nOnce you start practicing, this panel will give you personalized advice based on your actual performance. Try:\n- Asking the tutor a question about a Python topic\n- Attempting a quiz question\n- Running some code in the editor`,
          loading: false
        });
        return;
      }

      // --- Data sparsity warning ---
      const totalQuestions = Object.values(quizStats).reduce((sum, s) => sum + s.correct + s.wrong, 0);
      const totalRuns = struggleLogs.length;
      let dataWarning = '';
      if (totalQuestions < 5 && totalRuns < 5) {
        dataWarning = '\n⚠️ DATA WARNING: Very little data available (fewer than 5 quiz answers and 5 code runs). Be honest about limited data — do NOT fabricate patterns. Give general encouragement and suggest the student practice more.\n';
      } else if (totalQuestions < 10 || totalRuns < 10) {
        dataWarning = '\n⚠️ DATA WARNING: Limited data available. Be cautious about drawing strong conclusions — acknowledge uncertainty where relevant.\n';
      }

      // --- Call gpt-4o-mini ---
      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          temperature: 0.2,
          max_tokens: 700,
          messages: [{
            role: 'user',
            content: `You are a PySage Tutor giving personalized study advice to a student.
${dataWarning}
QUIZ & Q&A PERFORMANCE (recent 50 records, sorted worst→best accuracy):
${quizSummary}

CODE EXECUTION (recent 100 runs):
${codeSummary}

Q&A PRACTICE (last 50 interactions — subjects where student answered wrong):
${qaSummary}

RECENT WRONG ANSWERS (last 15):
${wrongSummary}

Write a concise, encouraging one-page study guide for this student. Include:
1. **Top 2-3 weaknesses** with specific, actionable advice for each
2. **One strength** to acknowledge
3. **Recommended next steps** (2-3 concrete actions)

Use markdown. Do NOT include a top-level title or heading like "Study Guide" at the start — begin directly with the content.
Keep it warm, specific, and practical. Do NOT repeat the raw data back.
IMPORTANT: Only suggest activities within this app. Do NOT recommend any external websites, tools, books, or videos.`
          }]
        })
      });

      if (!response.ok) throw new Error('API error');
      const data = await response.json();
      setAdvice({ content: data.choices[0]?.message?.content?.trim() || 'Could not generate advice.', loading: false });
    } catch (e) {
      console.error('StudyAdvicePanel error:', e);
      setAdvice({ content: 'Sorry, could not generate study advice. Please try again.', loading: false });
    }
  };

  // Auto-generate on mount
  React.useEffect(() => { generate(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: '#000', border: '2px solid rgba(254,240,138,0.7)',
          borderRadius: '12px', padding: '28px 32px', maxWidth: '680px', width: '92%',
          maxHeight: '80vh', overflowY: 'auto', position: 'relative',
          boxShadow: '0 20px 60px rgba(0,0,0,0.8)', color: '#fefce8'
        }}
      >
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: '14px', right: '16px', background: 'none', border: 'none', color: '#fef08a', fontSize: '20px', cursor: 'pointer' }}
        >✕</button>
        <h3 style={{ margin: '0 0 6px 0', color: '#fef08a', fontSize: '29px', fontWeight: 'bold' }}>🎯 Your Study Advice</h3>
        <p style={{ margin: '0 0 18px 0', color: '#f87171', fontSize: '12px', fontStyle: 'italic' }}>⚠️ This advice is AI-generated and may not be 100% accurate — use it as a guide, not a guarantee.</p>
        {!advice || advice.loading ? (
          <p style={{ color: '#fefce8', textAlign: 'center', padding: '40px 0' }}>⏳ Analyzing your performance…</p>
        ) : (
          <div className="study-advice-content">
            <MessageContent content={advice.content} />
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyAdvicePanel;
